import { gql, useQuery } from '@apollo/client';
import React from 'react';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type {
  PostTemplateBarQuery,
  PostTemplateBarQueryVariables,
} from '../graphql/generated/graphqlTypes';
import publicIcon from '../icons/public.svg';
import templateIcon from '../icons/template.svg';
import { UserRole } from '../topic/topicPolicyCheck';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import { createPostPath } from '../utils/url/postPath';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from './postPolicyCheck';
import PostViewStateBar from './PostViewStateBar';

const POST_QUERY = gql`
  query PostTemplateBar($id: ID!) {
    post(id: $id) {
      id
      ...isAllowedThroughLinkAccess_post
    }
  }
  ${isAllowedThroughLinkAccess.fragments.post}
`;

interface PostTemplateBarProps {
  postId: string;
}

const PostTemplateBar = ({ postId }: PostTemplateBarProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const { data } = useQuery<
    PostTemplateBarQuery,
    PostTemplateBarQueryVariables
  >(POST_QUERY, { variables: { id: postId } });
  const post = data?.post;

  if (!post) return null;

  const isPublic = isAllowedThroughLinkAccess(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Anonymous,
  );

  return (
    <PostViewStateBar
      actions={
        <Button
          variant="primary"
          to={createPostPath({ templateId: postId })}
          size="small"
        >
          Create post
        </Button>
      }
    >
      <SVGIcon>{templateIcon}</SVGIcon>
      <strong>Template</strong>
      {!isMobile && <span>Create new posts from this template’s contents</span>}
      {isPublic && (
        <Tooltip
          tooltip="Logged out visitors can access this template with its URL"
          placement="bottom"
          trigger="hover"
        >
          <SVGIcon>{publicIcon}</SVGIcon>
        </Tooltip>
      )}
    </PostViewStateBar>
  );
};

export default PostTemplateBar;
